import { slugify } from 'transliteration';
import cloneDeep from 'lodash.clonedeep';
import isBoolean from '../../../utils/is-boolean';
import actionTypes from '../constants/action-types';
import extractTitle from '../helpers/extract-title';
import createPageSource from '../helpers/create-page-source';
import generateUniquePath from '../helpers/generate-unique-path';
import replaceTitleInHtml from '../helpers/replace-title-in-html';
import globals from '../../globals';
import initialData from '../../saving/utils/initial-data';
import verifiers from '../verifiers';
import replaceBlocksIdInHtml from '../helpers/replace-blocks-id-in-html';

function slugifyPageTitle(title) {
  return slugify(title, {
    ignore: title.match(/\p{L}/gu) || [],
    allowedChars: 'a-zA-Z0-9-_.',
  });
}

const pages = (state = [], action) => {
  switch (action.type) {
    case actionTypes.RELOAD_SITE_META:
    case actionTypes.CONNECT_SITE: {
      return action.payload.site.meta.pages.map((page) => ({
        order: '0',
        displayInNavigation: false,
        ...page,
        name: page.name || page.title,
      }));
    }

    case actionTypes.SET_HOME_PAGE: {
      const { pageId, isMultilingualSite, locale, languageCodes } = action.payload;
      const nextHomePage = state.find((page) => page.id === pageId);

      if (!nextHomePage || nextHomePage.locale !== locale) {
        return state;
      }

      return state.map((page) => {
        if (page.id === pageId && page.locale === locale) {
          return {
            ...page,
            path: isMultilingualSite ? `/${locale}/` : '/',
          };
        }

        if (verifiers.isHomePage(page.path, languageCodes) && page.locale === locale) {
          return {
            ...page,
            path: generateUniquePath(
              state,
              slugifyPageTitle(page.title),
              isMultilingualSite ? locale : null
            ),
          };
        }

        return page;
      });
    }

    case actionTypes.DELETE_PAGE: {
      const { languageCodes } = action.payload;
      const pagesCount = state.length;

      if (pagesCount === 1) {
        return state;
      }

      const newState = state.filter(
        (page) =>
          !(page.id === action.payload.pageId && !verifiers.isHomePage(page.path, languageCodes))
      );

      if (newState.length !== pagesCount) {
        return newState;
      }

      return state;
    }

    case actionTypes.CREATE_PAGE: {
      const {
        html,
        name,
        pageId,
        displayInNavigation,
        locale,
        usedBlocks,
        pageTemplateId,
        recipeId,
        isMultilingualSite,
        displayBusinessName,
      } = action.payload;
      const title = extractTitle(html);

      return [
        ...state,
        {
          id: pageId,
          name: name || title,
          title,
          html,
          displayInNavigation,
          htmlCheckpoint: html,
          path: generateUniquePath(
            state,
            slugifyPageTitle(title),
            isMultilingualSite ? locale : null
          ),
          source: createPageSource([...state, ...initialData.getPages()], locale),
          locale,
          htmlLocale: locale,
          usedBlocks,
          order: '0',
          hasGlobals: globals.verifiers.hasGlobals(html),
          ...(pageTemplateId && { templateId: pageTemplateId }),
          ...(recipeId && { recipeId }),
          ...(isBoolean(displayBusinessName) && { displayBusinessName }),
        },
      ];
    }
    case actionTypes.COPY_PAGE: {
      const { targetPageId, name, title, displayInNavigation, pageId, locale, isMultilingualSite } =
        action.payload;
      const targetPage = cloneDeep(state.find((page) => page.id === targetPageId));
      const html = replaceBlocksIdInHtml(replaceTitleInHtml(targetPage.html, title));

      return [
        ...state,
        {
          ...targetPage,
          id: pageId,
          name: name || title,
          title,
          html,
          displayInNavigation,
          htmlCheckpoint: html,
          path: generateUniquePath(
            state,
            slugifyPageTitle(title),
            isMultilingualSite ? targetPage.locale : null
          ),
          source: createPageSource([...state, ...initialData.getPages()], locale),
          order: '0',
        },
      ];
    }
    case actionTypes.TRANSLATE_PAGE: {
      const {
        targetPageId,
        pageId,
        locale,
        title,
        name,
        displayInNavigation,
        isMultilingualSite,
        options,
      } = action.payload;
      const { html, pageIdToOverride, languageCodes } = options;
      const targetPage = cloneDeep(state.find((page) => page.id === targetPageId));
      const newHtml = html || targetPage.html;
      const newPageId = pageIdToOverride || pageId;
      const updatedNewHtml = replaceBlocksIdInHtml(
        replaceTitleInHtml(newHtml, title),
        targetPageId,
        newPageId
      );

      if (pageIdToOverride) {
        return state.map((page) => {
          if (page.id === pageIdToOverride) {
            const path = verifiers.isHomePage(page.path, languageCodes)
              ? page.path
              : generateUniquePath(state, slugifyPageTitle(title), locale);

            return {
              ...page,
              html: updatedNewHtml,
              htmlCheckpoint: updatedNewHtml,
              title,
              name: name || title,
              htmlLocale: locale,
              path,
            };
          }

          return page;
        });
      }

      return [
        ...state,
        {
          ...targetPage,
          id: pageId,
          title,
          name: name || title,
          html: updatedNewHtml,
          locale,
          htmlLocale: locale,
          displayInNavigation,
          htmlCheckpoint: updatedNewHtml,
          path: generateUniquePath(
            state,
            slugifyPageTitle(title),
            isMultilingualSite ? locale : null
          ),
          source: createPageSource([...state, ...initialData.getPages()], locale),
          order: '0',
        },
      ];
    }

    case actionTypes.CHANGE_PAGES_ORDER: {
      return state.map((page) => {
        const targetPage = action.payload.pages.find(({ pageId }) => pageId === page.id);

        if (targetPage) {
          return { ...page, order: targetPage.order };
        }

        return page;
      });
    }

    case actionTypes.SET_PAGES_IN_NAVIGATION: {
      return state.map((page) => {
        const isPageInNavigation = action.payload.pageIds.includes(page.id);

        return { ...page, displayInNavigation: isPageInNavigation };
      });
    }

    case actionTypes.SET_PAGES: {
      return [...action.payload.pages];
    }

    default:
      return state;
  }
};

export default pages;
